import {useEffect, useState} from 'react'
import * as Yup from 'yup'
import clsx from 'clsx'
import {Link, useNavigate, useParams} from 'react-router-dom'
import {useFormik} from 'formik'
import {useAuth} from '../core/Auth'
import CommonLogin from '../../../../_metronic/helpers/components/login-components/CommonLogin'
import {SEND_EMAIL_FOR_ACCOUNT_ACTIVATION} from '../../../../utils/GraphQLAPI'
import {useMutation} from '@apollo/client'
import withReactContent from 'sweetalert2-react-content'
import Swal from 'sweetalert2'
import {navigateUser} from '../../../../utils/helper'
import '../../../../_metronic/assets/sass/components/AccountActivation.scss'
import ContactTPVAdmin from './ContactTPVAdmin'
import ShowHidePwd from '../../../sharedComponent/ShowHidePwd/ShowHidePwd'
import {graphql} from 'graphql'

const MySwal = withReactContent(Swal)

const accountActivationSchema = Yup.object().shape({
  email: Yup.string()
    .email('Wrong email format')
    .min(3, 'Minimum 3 character is required')
    .max(50, 'Maximum 50 symbols')
    .required('Email is required'),
    password: Yup.string()
    .min(8, 'Minimum 8 characters are required')
    .max(50, 'Maximum 50 symbols')
    .matches(/^(?=.*[A-Za-z])(?=.*\d)(?=.*[^A-Za-z\d])[A-Za-z\d`~!@#$%^&*()_+=\-{}|[\]:;"'<>,.?\/]{8,}$/,
      'Password must contain at least 8 characters, including one letter, one number, and one symbol'
  )
    .required('Password is required'),
  changePassword: Yup.string()
    .required('Password confirmation is required')
    .when('password', {
      is: (val: string) => (val && val.length > 0 ? true : false),
      then: Yup.string().oneOf([Yup.ref('password')], "Password and Confirm Password didn't match"),
    }),
  privacyPolicy: Yup.boolean()
    .oneOf([true], 'You must accept the privacy policy')
    .required('privacy policy is required'),
})

const initialValues = {
  email: '',
  password: '',
  changePassword: '',
  privacyPolicy: true,
}

/*
  Formik+YUP+Typescript:
  https://jaredpalmer.com/formik/docs/tutorial#getfieldprops
  https://medium.com/@maurice.de.beijer/yup-validation-and-typescript-and-formik-6c342578a20e
*/

export function AccountActivation() {
  const [loading, setLoading] = useState(false)
  const {saveAuth} = useAuth()
  const [subheader, setSubHeader] = useState<string>()
  const [passwordType, setPasswordType] = useState<string>('password')
  const [repeatPasswordType, setRepeatPasswordType] = useState<string>('password')
  const [roles, setRoles] = useState<any>()
  const navigate = useNavigate()
  const getUrlParams = useParams()
  const TEXT = 'text'
  const PWD = 'password'
  const [userRole, setUserRole] = useState<any>()

  useEffect(() => {
    navigateUser(getUrlParams.adminUser, setSubHeader, setUserRole, setRoles)
  }, [getUrlParams])

  const [accountActivation] = useMutation(SEND_EMAIL_FOR_ACCOUNT_ACTIVATION, {
    onCompleted: (data) => {
      if (data.sendVerficationCodeMail === 'Email Sent Successfully!') {
        navigate(`/${getUrlParams.adminUser}/authentication`)
      }
      localStorage.removeItem('_accesstoken')
    },
    onError: (e) => {
      setLoading(false)
      console.log(e)
      MySwal.fire({
        icon: 'error',
        title: 'Error',
        text: e.message,
      })
    },
  })

  const formik = useFormik({
    initialValues,
    validationSchema: accountActivationSchema,
    onSubmit: async (values, {setStatus, setSubmitting}) => {
      setLoading(true)
      try {
        accountActivation({
          variables: {
            emailId: values.email.toLowerCase(),
            password: values.password,
            role: roles,
          },
        })
        localStorage.setItem('adminActivationEmail', values.email.toLowerCase())
      } catch (error) {
        saveAuth(undefined)
        setStatus('The login details are incorrect')
        setSubmitting(false)
        setLoading(false)
      }
    },
  })

  const showPassword = () => {
    const tempPwdType: string = passwordType === PWD ? TEXT : PWD
    setPasswordType(tempPwdType)
  }

  const showRepeatPassword = () => {
    const tempRepeatPwdType: string = repeatPasswordType === PWD ? TEXT : PWD
    setRepeatPasswordType(tempRepeatPwdType)
  }

  const transparentBg = 'form-control bg-transparent'

  return (
    <div
      className='d-flex flex-column flex-lg-row flex-column-fluid h-100 vh-100'
      style={{
        backgroundImage: 'linear-gradient(rgb(35, 56, 119), rgb(12, 20, 43))',
        backgroundSize: 'cover',
      }}
    >
      <div className='d-flex flex-column mx-10'>
        <div className='d-flex flex-column mx-10'>
          <CommonLogin role={subheader} userRole={userRole} />
        </div>
      </div>

      <div className='d-flex align-items-center justify-content-center flex-lg-row-fluid w-lg-25 mt-5 order-1 order-lg-2'>
        <div className='card card-custom px-6 py-6 ml-5 bg-white h-70 w-500px'>
          <form
            className='form'
            onSubmit={formik.handleSubmit}
            noValidate
            id='kt_login_signin_form'
          >
            <div className='mt-5'>
              <div className='text-center'>
                <h1 className='text-black fw-bolde'>Account Activation</h1>
              </div>
              <h1 className='text-gray-500 fw-semibold fs-6 text-center'>
                Activate your account to Sign In
              </h1>
            </div>

            <div className='p-5'>
              {/* Email */}
              <div className='fv-row mb-8'>
                <input
                  placeholder='Email'
                  {...formik.getFieldProps('email')}
                  className={clsx(
                    transparentBg,
                    {'is-invalid': formik.touched.email && formik.errors.email},
                    {'is-valid': formik.touched.email && !formik.errors.email}
                  )}
                  type='email'
                  name='email'
                  autoComplete='off'
                />
                {formik.touched.email && formik.errors.email && (
                  <div className='fv-plugins-message-container'>
                    <div className='fv-help-block'>
                      <div role='alert'>{formik.errors.email}</div>
                    </div>
                  </div>
                )}
              </div>

              {/* Password */}
              <div className='fv-row mb-8'>
                <div className='position-relative'>
                  <input
                    type={passwordType}
                    placeholder='Password'
                    autoComplete='off'
                    {...formik.getFieldProps(PWD)}
                    className={clsx(
                      transparentBg,
                      {'is-invalid': formik.touched.password && formik.errors.password},
                      {'is-valid': formik.touched.password && !formik.errors.password}
                    )}
                  />
                  <ShowHidePwd passwordType={passwordType} showPassword={showPassword} />
                </div>
                {formik.touched.password && formik.errors.password && (
                  <div className='fv-plugins-message-container'>
                    <div className='fv-help-block'>
                      <div role='alert'>{formik.errors.password}</div>
                    </div>
                  </div>
                )}
              </div>

              {/* Repeat Password */}
              <div className='fv-row mb-8'>
                <div className='position-relative'>
                  <input
                    type={repeatPasswordType}
                    autoComplete='off'
                    placeholder='Repeat Password'
                    {...formik.getFieldProps('changePassword')}
                    className={clsx(
                      transparentBg,
                      {'is-invalid': formik.touched.changePassword && formik.errors.changePassword},
                      {'is-valid': formik.touched.changePassword && !formik.errors.changePassword}
                    )}
                  />
                  <ShowHidePwd
                    passwordType={repeatPasswordType}
                    showPassword={showRepeatPassword}
                  />
                </div>
                {formik.touched.changePassword && formik.errors.changePassword && (
                  <div className='fv-plugins-message-container'>
                    <div className='fv-help-block'>
                      <div role='alert'>{formik.errors.changePassword}</div>
                    </div>
                  </div>
                )}
              </div>

              {/* Privacy Policy */}
              <div className='fv-row mb-8 '>
                <div className='position-relative form-check form-check-solid form-check-custom'>
                  <input
                    type='checkbox'
                    defaultChecked={true}
                    {...formik.getFieldProps('privacyPolicy')}
                    className='form-check-input text-muted-dark h-20px w-20px'
                  />
                  <label className='form-check-label fs-13px fw-semibold text-dark'>
                    I Accept the{' '}
                    <Link to='' className='link-primary'>
                      Privacy Policy
                    </Link>
                  </label>
                </div>
                {formik.touched.privacyPolicy && formik.errors.privacyPolicy && (
                  <div className='fv-plugins-message-container'>
                    <div className='fv-help-block'>
                      <div role='alert'>{formik.errors.privacyPolicy}</div>
                    </div>
                  </div>
                )}
              </div>

              {/* Submit */}
              <div className='d-flex mb-5 align-items-center justify-content-center'>
                <button
                  type='submit'
                  id='kt_sign_in_submit'
                  className='btn btn-primary btn-lg w-100'
                  disabled={formik.isSubmitting}
                >
                  {!loading && <span className='indicator-label fw-500'>Activate Now</span>}
                  {loading && (
                    <span className='indicator-progress' style={{display: 'block'}}>
                      Please wait...
                      <span className='spinner-border spinner-border-sm align-middle ms-2' />
                    </span>
                  )}
                </button>
              </div>

              <div className='text-gray-500 text-center fw-semibold fs-6 pb-0'>
                <ContactTPVAdmin />
              </div>
            </div>
          </form>
        </div>
      </div>
    </div>
  )
}
